import { toast, type ToastAction } from '../../../../components/ui/toaster';

/**
 * Shared "Export complete" toast for the Query Editor and Table Explorer
 * export menus (grid-parity rule: both grids present the same export
 * affordances). Adds a "Show in folder" action that reveals the written file
 * in the OS file manager — omitted in browser mode where the shim can't
 * reveal files.
 */
export function showExportSuccessToast(
  filePath: string | undefined,
  grid: 'query-editor' | 'table-explorer',
  description?: string
): void {
  toast.success({
    title: 'Export complete',
    description: description ?? filePath,
    ...(filePath ? showInFolderAction(filePath, grid) : {}),
  });
}

/**
 * Toast for a CSV export that failed mid-stream but kept the rows written so
 * far on disk (see `streamingCsvExport.ts`) — a 52%-complete multi-million-row
 * file is still worth having, so the user gets the same "Show in folder"
 * affordance as a successful export. When `onResume` is provided the toast
 * leads with a "Resume export" action that continues appending from the last
 * written row, and persists — the user may have walked away from a
 * multi-minute export and must not miss the offer.
 */
export function showExportPartialKeptToast(
  filePath: string,
  rowCount: number,
  error: string,
  grid: 'query-editor' | 'table-explorer',
  onResume?: () => void
): void {
  const description = `${error}. The first ${rowCount.toLocaleString()} rows were saved.`;
  if (onResume) {
    const folder = showInFolderAction(filePath, grid);
    toast.warning({
      title: 'Export incomplete — partial file kept',
      description,
      persist: true,
      actions: [
        { label: 'Resume export', onClick: onResume },
        ...(folder.action ? [folder.action] : []),
      ],
    });
    return;
  }
  toast.warning({
    title: 'Export incomplete — partial file kept',
    description,
    duration: 30_000,
    ...showInFolderAction(filePath, grid),
  });
}

/** How many skipped row numbers are named before the list collapses to "+K more". */
const MAX_LISTED_SKIPPED_ROWS = 5;

/**
 * Skipped row numbers as the user counts them: 1-BASED with locale separators
 * (the export-failure copy already says "Row 1,816,426" — a 0-based list
 * beside it would point at the wrong row), truncated to
 * `MAX_LISTED_SKIPPED_ROWS` so a pathological export can't produce a toast the
 * size of the window.
 */
export function formatSkippedRowNumbers(skippedRows: readonly number[]): string {
  const listed = skippedRows
    .slice(0, MAX_LISTED_SKIPPED_ROWS)
    .map((index) => (index + 1).toLocaleString())
    .join(', ');
  const rest = skippedRows.length - MAX_LISTED_SKIPPED_ROWS;
  return rest > 0 ? `${listed} (+${rest.toLocaleString()} more)` : listed;
}

/**
 * Body text for the skipped-rows toast: how much data actually landed in the
 * file out of the result set's row count, then which rows are missing.
 * `total` counts skipped positions too (cursor semantics — see
 * `ResultCacheExportCsvResponse.rowCount`), so `dataRows` is the number the
 * file really contains.
 */
export function describeSkippedRowsExport(
  dataRows: number,
  total: number,
  skippedRows: readonly number[]
): string {
  return (
    `${dataRows.toLocaleString()} of ${total.toLocaleString()} rows exported.\n` +
    `Could not be read from the server — ${skippedRows.length === 1 ? 'row' : 'rows'} ` +
    formatSkippedRowNumbers(skippedRows)
  );
}

/**
 * Toast for an export that completed but silently left rows out — the subset
 * path skips a row it cannot read rather than failing the whole export, so the
 * file on disk is short. A green "Export complete" here would be a lie by
 * omission: this is a persistent WARNING naming the missing rows, with the same
 * "Show in folder" affordance as its siblings. Callers must only use it when
 * `skippedRows` is non-empty.
 *
 * The leading action depends on how many rows are missing:
 * - ONE skipped row → "Show row N" (1-based, matching the body text) jumps the
 *   results grid straight to it. `keepOpen`: a missing-data warning should only
 *   leave via explicit dismissal, not as the side effect of using it.
 * - SEVERAL → "Review skipped rows" opens the durable "Skipped rows" bottom tab
 *   listing every missing row (see `SkippedRowsPanel.tsx`). Deliberately NOT
 *   `keepOpen`: the click lands the user on a surface that outlives the toast,
 *   so the toast has done its job. (A per-click stepping button was rejected —
 *   a button that mutates its target per click is an invisible state machine.)
 *
 * When the relevant callback for the count is missing the toast falls back to
 * the single "Show in folder" shape. The grid owns the jump/review behavior
 * (and the "that result set is gone" fallback); see `useWindowedGrid.ts`.
 */
export function showExportSkippedRowsToast(
  filePath: string | undefined,
  dataRows: number,
  total: number,
  skippedRows: readonly number[],
  grid: 'query-editor' | 'table-explorer',
  onShowRow?: (rowIndex: number) => void,
  onReviewSkippedRows?: () => void
): void {
  const count = skippedRows.length;
  const shared = {
    title: `Export finished — ${count.toLocaleString()} ${count === 1 ? 'row' : 'rows'} skipped`,
    description: describeSkippedRowsExport(dataRows, total, skippedRows),
    // Missing data must survive the 5s auto-dismiss — a user who walked away
    // from a multi-minute export would otherwise never learn about it.
    persist: true,
  };
  const folder = filePath ? showInFolderAction(filePath, grid) : {};
  const lead: ToastAction | null =
    count === 1 && onShowRow
      ? {
          label: `Show row ${(skippedRows[0] + 1).toLocaleString()}`,
          keepOpen: true,
          onClick: () => {
            window.electronAPI?.analytics
              ?.track('feature:export_skipped_row_jump', { grid, source: 'toast' })
              .catch(() => {});
            onShowRow(skippedRows[0]);
          },
        }
      : count >= 2 && onReviewSkippedRows
        ? {
            label: 'Review skipped rows',
            onClick: () => {
              window.electronAPI?.analytics
                ?.track('feature:export_skipped_rows_review', { grid })
                .catch(() => {});
              onReviewSkippedRows();
            },
          }
        : null;
  if (!lead) {
    toast.warning({ ...shared, ...folder });
    return;
  }
  toast.warning({
    ...shared,
    actions: [lead, ...(folder.action ? [{ ...folder.action, keepOpen: true }] : [])],
  });
}

function showInFolderAction(
  filePath: string,
  grid: 'query-editor' | 'table-explorer'
): { action?: { label: string; onClick: () => void } } {
  if (!window.electronAPI?.showItemInFolder) return {};
  return {
    action: {
      label: 'Show in folder',
      onClick: () => {
        window.electronAPI?.analytics
          ?.track('feature:export_show_in_folder', { grid })
          .catch(() => {});
        void window.electronAPI?.showItemInFolder?.(filePath).catch(() => {});
      },
    },
  };
}
